import { Spacer, Text } from "@hackthenorth/north";
import { DocumentPlusIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";
import styled from "styled-components";

import { Modal } from "src/shared/components";
import { mediaQueries } from "src/theme/deviceBreakpoints";
import { QuestType } from "src/views/goose-games/types";

import FooterActions from "../../../../footerActions";
import { MODAL_FIELDS } from "../constants";
import { parseQuestMeta, QuestMeta } from "../metadata";
import MetadataInput from "../MetadataInput";

export interface CreateModalProps {
  isOpen: boolean;
  onConfirm: (
    title: string,
    type: QuestType,
    pointsRewarded: number,
    completionLimit: number,
    successMessage: string,
    errorMessage: string,
    flash: boolean,
    hidden: boolean,
    eventCategory?: string,
    description?: string,
    questionString?: string,
    questionAnswer?: string,
    meta?: QuestMeta
  ) => void;
  onCancel: () => void;
}
const CreateQuestModal: React.FC<CreateModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
}) => {
  const field = MODAL_FIELDS["QUEST"].create;
  const [title, setTitle] = useState("");
  const [questType, setQuestType] = useState<QuestType>(QuestType.Question);
  const [limit, setLimit] = useState<number>(1);
  const [points, setPoints] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [answer, setAnswer] = useState<string | undefined>(undefined);
  const [isFlash, setIsFlash] = useState<boolean>(false);
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const [metadata, setMetadata] = useState("");
  const parsedMeta = parseQuestMeta(metadata);

  const baseFormValues = {
    title,
    questType,
    limit,
    points,
    description,
  };

  const questionFormValues = {
    answer,
  };

  const hasNonEmptyRequiredFields =
    baseFormValues.title &&
    baseFormValues.questType &&
    baseFormValues.limit > 0 &&
    baseFormValues.points &&
    baseFormValues.points &&
    Number(baseFormValues.points) > 0;

  const hasNonEmptyQuestionRequiredFields = !!questionFormValues.answer;

  const isMultiRedeemable =
    questType !== QuestType.Question &&
    questType !== QuestType.OrganizerApproved;

  const updateQuestType = (input: QuestType) => {
    setQuestType(input);
    setLimit(1);
    setAnswer(undefined);
  };

  const resetFields = () => {
    setMetadata("");
    setTitle("");
    setQuestType(QuestType.Question);
    setLimit(1);
    setPoints(undefined);
    setDescription(undefined);
    setAnswer(undefined);
    setIsFlash(false);
    setIsHidden(false);
  };

  const handleConfirmation = () => {
    if (
      hasNonEmptyRequiredFields &&
      !parsedMeta.error &&
      (questType === QuestType.Question
        ? hasNonEmptyQuestionRequiredFields
        : true)
    ) {
      onConfirm(
        title,
        questType,
        points ? Number(points) : 0,
        limit,
        field.success,
        field.error,
        isFlash,
        isHidden,
        undefined,
        description,
        questType === QuestType.Question ? title : undefined,
        questType === QuestType.Question ? answer : undefined,
        parsedMeta.value
      );
      resetFields();
    }
  };

  return (
    <Modal isOpen={isOpen} width="600px">
      <HeaderContainer>
        <HeaderIcon>
          <DocumentPlusIcon />
        </HeaderIcon>
        <Text mods="h3 bold">{field.title}</Text>
      </HeaderContainer>
      <FormContainer>
        <FirstLayer>
          <NameWrapper>
            <Text mods="body bold">
              {field.name[questType !== QuestType.Question ? 0 : 1]}
            </Text>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={field.name_placeholder}
              style={{ width: "100%", padding: "8px", borderRadius: "4px" }}
            />
          </NameWrapper>
          <DropdownWrapper>
            <Text mods="body bold">{field.type}</Text>
            <select
              value={questType}
              onChange={(e) => updateQuestType(e.target.value as QuestType)}
              onBlur={(e) => updateQuestType(e.target.value as QuestType)}
              style={{ width: "100%", padding: "8px", borderRadius: "4px" }}
            >
              {(
                field.type_options as Array<{ label: string; value: string }>
              ).map((opt, i) => (
                <option key={i} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </DropdownWrapper>
        </FirstLayer>

        <SecondLayer>
          <TogglesRow>
            <ToggleWrapper>
              <Text mods="body bold">Feature as &quot;Flash&quot; Quest</Text>
              <ToggleTrack
                type="button"
                role="switch"
                aria-checked={isFlash}
                aria-label="Toggle flash quest"
                isOn={isFlash}
                onClick={() => setIsFlash((prev) => !prev)}
              >
                <ToggleKnob isOn={isFlash} />
              </ToggleTrack>
            </ToggleWrapper>
            <ToggleWrapper>
              <Text mods="body bold">Hide Quest from Hackers</Text>
              <ToggleTrack
                type="button"
                role="switch"
                aria-checked={isHidden}
                aria-label="Hide quest"
                isOn={isHidden}
                onClick={() => setIsHidden((prev) => !prev)}
              >
                <ToggleKnob isOn={isHidden} />
              </ToggleTrack>
            </ToggleWrapper>
          </TogglesRow>

          <DescriptionWrapper>
            <Text mods="body bold">Description</Text>
            <DescriptionTextarea
              placeholder="Give a short 1-2 sentence description..."
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </DescriptionWrapper>

          {questType === QuestType.Question && (
            <DescriptionWrapper>
              <Text mods="body bold">Answer</Text>
              <DescriptionTextarea
                placeholder="Enter the answer..."
                rows={4}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
              />
            </DescriptionWrapper>
          )}
        </SecondLayer>

        <ThirdLayer>
          {isMultiRedeemable && (
            <CounterWrapper>
              <Text mods="body bold">Maximum redeemable times</Text>
              <CounterContainer>
                <CounterButton
                  onClick={() =>
                    setLimit((prev) => (prev > 1 ? prev - 1 : prev))
                  }
                >
                  −
                </CounterButton>
                <CounterValue>{limit}</CounterValue>
                <CounterButton onClick={() => setLimit((prev) => prev + 1)}>
                  +
                </CounterButton>
              </CounterContainer>
            </CounterWrapper>
          )}
          <NumberWrapper>
            <Text mods="body bold">{field.num_input}</Text>
            <input
              type="number"
              min="1"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              placeholder={field.num_input_placeholder}
              style={{ width: "100%", padding: "8px", borderRadius: "4px" }}
            />
          </NumberWrapper>
        </ThirdLayer>
        <MetadataInput
          value={metadata}
          onChange={setMetadata}
          error={parsedMeta.error}
        />
      </FormContainer>
      <Spacer height={36} />
      <FooterActions
        confirmText={"Create"}
        onConfirm={handleConfirmation}
        onCancel={onCancel}
        isConfirmDisabled={
          !!parsedMeta.error ||
          !(
            hasNonEmptyRequiredFields &&
            (questType === QuestType.Question
              ? hasNonEmptyQuestionRequiredFields
              : true)
          )
        }
      />
    </Modal>
  );
};

export default CreateQuestModal;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 24px;
  gap: 8px;
`;

const HeaderIcon = styled.div<{ backgroundColor?: string }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: 50%;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FirstLayer = styled.div`
  display: flex;
  gap: 20px;

  ${mediaQueries.largeMobile} {
    flex-direction: column;
  }
`;

const NameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const DropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const SecondLayer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ToggleWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const TogglesRow = styled.div`
  display: flex;
  gap: 20px;
`;

const ToggleTrack = styled.button<{ isOn: boolean }>`
  position: relative;
  width: 52px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 14px;
  background: ${({ theme, isOn }) =>
    isOn
      ? theme.globalConstants.color.borderPrimary
      : theme.globalConstants.color.borderGray};
  cursor: pointer;
  transition: background-color 0.2s ease;
`;

const ToggleKnob = styled.div<{ isOn: boolean }>`
  position: absolute;
  top: 3px;
  left: ${({ isOn }) => (isOn ? "27px" : "3px")};
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: left 0.2s ease;
`;

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DescriptionTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;

  &::placeholder {
    color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.globalConstants.color.borderPrimary};
  }
`;

const ThirdLayer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;

  ${mediaQueries.largeMobile} {
    flex-direction: column;
  }
`;

const CounterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const CounterContainer = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  overflow: hidden;
`;

const CounterButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};

  &:hover {
    background-color: ${({ theme }) =>
      theme.globalConstants.color.backgroundGray};
  }
`;

const CounterValue = styled.div`
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderGray};
  border-right: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderGray};
  background-color: ${({ theme }) =>
    theme.globalConstants.color.backgroundLight};
`;

const NumberWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;
